import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, ArrowDown, Trash2, Upload, Loader2, Star, ImagePlus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Listings as ListingsApi } from '../../api/client';
import { useStore } from '../../store';

type ImageRecord = {
  id: number;
  etsy_image_id: number;
  rank: number;
  url_75: string | null;
  url_170: string | null;
  url_570: string | null;
  url_1140: string | null;
  alt_text: string | null;
};

type Props = {
  listingId: number;
  images: ImageRecord[];
  /** called when user finishes a destructive op so parent can refetch the listing detail */
  onChanged: () => void;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB decoded
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function ListingImagesManager({ listingId, images, onChanged }: Props) {
  const pushToast = useStore((s) => s.pushToast);
  const [localImages, setLocalImages] = useState<ImageRecord[]>([]);
  const [uploading, setUploading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null); // image id being deleted
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragIdRef = useRef<number | null>(null); // for drag-and-drop

  // Sync local state from prop. Order is by rank asc.
  useEffect(() => {
    setLocalImages([...images].sort((a, b) => a.rank - b.rank));
  }, [images]);

  const persistOrder = async (next: ImageRecord[]) => {
    setLocalImages(next);
    setReordering(true);
    try {
      const r = await ListingsApi.reorderImages(
        listingId,
        next.map((i) => i.etsy_image_id),
      );
      pushToast({ kind: 'success', message: `Reordered ${r.reordered} images` });
      onChanged();
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
      // Rollback: refetch from server
      onChanged();
    } finally {
      setReordering(false);
    }
  };

  const moveUp = async (idx: number) => {
    if (idx <= 0) return;
    const next = [...localImages];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    await persistOrder(next);
  };

  const moveDown = async (idx: number) => {
    if (idx >= localImages.length - 1) return;
    const next = [...localImages];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    await persistOrder(next);
  };

  // Native HTML5 drag-and-drop for desktop users. Mobile users use arrows.
  const onDragStart = (e: React.DragEvent, id: number) => {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = async (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    const sourceId = Number(dragIdRef.current);
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const srcIdx = localImages.findIndex((i) => i.etsy_image_id === sourceId);
    const tgtIdx = localImages.findIndex((i) => i.etsy_image_id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const next = [...localImages];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    await persistOrder(next);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    let lastRank = localImages.length > 0 ? Math.max(...localImages.map((i) => i.rank)) + 1 : 1;
    let uploadedCount = 0;
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        pushToast({ kind: 'error', message: `${file.name}: file too large (>10MB)` });
        continue;
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        pushToast({ kind: 'error', message: `${file.name}: unsupported type ${file.type}` });
        continue;
      }
      try {
        const buf = await file.arrayBuffer();
        const data_base64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
        const r = await ListingsApi.uploadImage(listingId, {
          data_base64,
          filename: file.name,
          rank: lastRank++,
        });
        uploadedCount++;
        pushToast({ kind: 'success', message: `Uploaded ${file.name}` });
      } catch (e) {
        pushToast({ kind: 'error', message: `${file.name}: ${(e as Error).message}` });
      }
    }
    setUploading(false);
    if (uploadedCount > 0) onChanged();
  };

  const handleDelete = async (img: ImageRecord) => {
    if (!confirm(`Delete image #${img.rank}? This cannot be undone.`)) return;
    setBusyId(img.etsy_image_id);
    try {
      await ListingsApi.deleteImage(listingId, img.etsy_image_id);
      pushToast({ kind: 'success', message: `Deleted image #${img.rank}` });
      onChanged();
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="kicker">// images ({localImages.length})</div>
          <div className="text-[11px] text-ink-500 mt-0.5">
            Drag to reorder, or use arrows. Image #1 is the hero shot.
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
          />
          <Button
            variant="primary"
            size="sm"
            leftIcon={uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </div>

      {/* Grid */}
      {localImages.length === 0 ? (
        <div className="surface-soft p-6 text-center border-dashed border-cream-300 rounded-xl">
          <ImagePlus size={20} className="mx-auto mb-2 text-ink-300" />
          <div className="text-[12.5px] text-ink-600">No images yet. Click Upload to add the first one.</div>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          <AnimatePresence>
            {localImages.map((img, idx) => (
              <motion.div
                key={img.etsy_image_id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.18 }}
                draggable
                onDragStart={(e) => onDragStart(e as unknown as React.DragEvent, img.etsy_image_id)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e as unknown as React.DragEvent, img.etsy_image_id)}
                className={`group relative rounded-lg overflow-hidden border bg-cream-100 cursor-grab active:cursor-grabbing ${reordering ? 'opacity-60' : ''}`}
                style={{ aspectRatio: '1 / 1' }}
              >
                {/* Image */}
                {img.url_170 || img.url_570 ? (
                  <img
                    src={img.url_570 || img.url_170 || ''}
                    alt={img.alt_text || `Listing image ${img.rank}`}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-ink-400 text-[10px]">
                    no preview
                  </div>
                )}

                {/* Rank badge */}
                <div className="absolute top-1 left-1 px-1.5 h-5 rounded-md bg-ink-800/80 text-white text-[10px] font-mono flex items-center gap-1">
                  {idx === 0 && <Star size={9} className="text-amber fill-amber" />}
                  #{img.rank}
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-ink-800/0 group-hover:bg-ink-800/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0 || reordering}
                      className="h-7 w-7 rounded-md bg-white text-ink-800 hover:bg-etsy hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-ink-800 flex items-center justify-center shadow"
                      title="Move up"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => moveDown(idx)}
                      disabled={idx === localImages.length - 1 || reordering}
                      className="h-7 w-7 rounded-md bg-white text-ink-800 hover:bg-etsy hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-ink-800 flex items-center justify-center shadow"
                      title="Move down"
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(img)}
                      disabled={busyId === img.etsy_image_id}
                      className="h-7 w-7 rounded-md bg-white text-rose-500 hover:bg-rose-500 hover:text-white flex items-center justify-center shadow disabled:opacity-40"
                      title="Delete"
                    >
                      {busyId === img.etsy_image_id
                        ? <Loader2 size={12} className="animate-spin" />
                        : <Trash2 size={12} />}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
